import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Paper,
  TextField,
  Tooltip,
  Typography
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import EditIcon from "@material-ui/icons/Edit";
import HistoryIcon from "@material-ui/icons/History";
import SaveIcon from "@material-ui/icons/Save";
import CloseIcon from "@material-ui/icons/Close";
import { toast } from "react-toastify";

import api from "../../services/api";
import toastError from "../../errors/toastError";

const useStyles = makeStyles((theme) => ({
  dialogPaper: {
    minWidth: 720,
    maxWidth: 860,
    [theme.breakpoints.down("sm")]: {
      minWidth: "auto",
      width: "100%",
      margin: theme.spacing(1)
    }
  },
  headerLine: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    marginBottom: theme.spacing(1)
  },
  formCard: {
    padding: theme.spacing(2),
    marginBottom: theme.spacing(2),
    borderRadius: 12,
    background: theme.palette.type === "dark"
      ? "linear-gradient(135deg, rgba(29,53,87,0.32), rgba(11,92,75,0.22))"
      : "linear-gradient(135deg, rgba(37,99,235,0.06), rgba(16,185,129,0.05))"
  },
  formGrid: {
    display: "grid",
    gridTemplateColumns: "1.4fr 0.8fr",
    gap: theme.spacing(2),
    [theme.breakpoints.down("sm")]: {
      gridTemplateColumns: "1fr"
    }
  },
  listWrap: {
    maxHeight: "55vh",
    overflowY: "auto",
    ...theme.scrollbarStyles
  },
  entryItem: {
    alignItems: "flex-start",
    paddingLeft: 0,
    paddingRight: 0
  },
  entryCard: {
    width: "100%",
    padding: theme.spacing(2),
    borderRadius: 12,
    border: `1px solid ${theme.palette.divider}`,
    backgroundColor: theme.palette.type === "dark" ? "rgba(255,255,255,0.02)" : "#fff"
  },
  entryHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    gap: theme.spacing(2),
    marginBottom: theme.spacing(1)
  },
  entryMeta: {
    display: "flex",
    flexWrap: "wrap",
    gap: theme.spacing(1),
    marginTop: theme.spacing(1)
  },
  entryNote: {
    whiteSpace: "pre-wrap",
    wordBreak: "break-word"
  },
  muted: {
    opacity: 0.74
  },
  entryActions: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1)
  }
}));

const formatDateTime = (value) => {
  if (!value) {
    return "Sin fecha";
  }

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return "Sin fecha";
  }

  return date.toLocaleString("es-CO");
};

const ContactHistoryModal = ({
  open,
  onClose,
  contact,
  ticket = null,
  allowWrite = false
}) => {
  const classes = useStyles();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [entries, setEntries] = useState([]);
  const [draft, setDraft] = useState({ note: "", eventAt: new Date().toISOString() });
  const [editingId, setEditingId] = useState(null);

  const ticketEditable = useMemo(() => {
    return allowWrite && ticket && ticket.status !== "closed";
  }, [allowWrite, ticket]);

  const loadEntries = useCallback(async () => {
    if (!contact?.id) {
      return;
    }

    setLoading(true);
    try {
      const { data } = await api.get(`/contacts/${contact.id}/history`);
      setEntries(Array.isArray(data) ? data : []);
    } catch (err) {
      toastError(err);
    } finally {
      setLoading(false);
    }
  }, [contact]);

  useEffect(() => {
    if (open) {
      setDraft({ note: "", eventAt: new Date().toISOString() });
      setEditingId(null);
      loadEntries();
    }
  }, [open, loadEntries]);

  const resetDraft = () => {
    setDraft({ note: "", eventAt: new Date().toISOString() });
    setEditingId(null);
  };

  const handleSave = async () => {
    const trimmedNote = String(draft.note || "").trim();
    if (!trimmedNote) {
      toast.warning("Escriba un apunte para guardar en la bitácora.");
      return;
    }

    if (!contact?.id) {
      return;
    }

    setSaving(true);
    try {
      const payload = {
        note: trimmedNote,
        ticketId: ticket?.id || null
      };

      if (editingId) {
        await api.put(`/contacts/${contact.id}/history/${editingId}`, payload);
        toast.success("Bitácora actualizada.");
      } else {
        await api.post(`/contacts/${contact.id}/history`, payload);
        toast.success("Apunte agregado a la bitácora.");
      }

      resetDraft();
      await loadEntries();
    } catch (err) {
      toastError(err);
    } finally {
      setSaving(false);
    }
  };

  const canEditEntry = (entry) => {
    if (!ticketEditable || !ticket?.id) {
      return false;
    }

    return entry.ticketId === ticket.id && entry.ticket?.status !== "closed";
  };

  const handleStartEdit = (entry) => {
    setEditingId(entry.id);
    setDraft({
      note: entry.note || "",
      eventAt: entry.eventAt || entry.createdAt || new Date().toISOString()
    });
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="md"
      classes={{ paper: classes.dialogPaper }}
    >
      <DialogTitle disableTypography>
        <div className={classes.headerLine}>
          <HistoryIcon />
          <div>
            <Typography variant="h6">Bitácora del cliente</Typography>
            <Typography variant="body2" className={classes.muted}>
              {contact?.name || "Sin contacto"}{contact?.number ? ` · ${contact.number}` : ""}
            </Typography>
          </div>
        </div>
      </DialogTitle>

      <DialogContent dividers>
        {ticketEditable && (
          <Paper elevation={0} className={classes.formCard}>
            <Typography variant="subtitle1" gutterBottom>
              {editingId ? "Corregir apunte actual" : "Agregar apunte general del cliente"}
            </Typography>
            <Typography variant="body2" className={classes.muted} gutterBottom>
              Este registro queda asociado al cliente y visible para otros usuarios al consultar su historial.
            </Typography>
            <div className={classes.formGrid}>
              <TextField
                label="Comentario"
                variant="outlined"
                multiline
                minRows={4}
                value={draft.note}
                onChange={(event) => setDraft((prev) => ({ ...prev, note: event.target.value }))}
                fullWidth
              />
              <TextField
                label="Fecha y hora"
                variant="outlined"
                value={formatDateTime(draft.eventAt)}
                InputLabelProps={{ shrink: true }}
                InputProps={{ readOnly: true }}
                fullWidth
              />
            </div>
            <div style={{ display: "flex", justifyContent: "flex-end", gap: 12, marginTop: 16 }}>
              <Button
                variant="outlined"
                startIcon={<CloseIcon />}
                onClick={resetDraft}
                disabled={saving}
              >
                Limpiar
              </Button>
              <Button
                color="primary"
                variant="contained"
                startIcon={<SaveIcon />}
                onClick={handleSave}
                disabled={saving}
              >
                {editingId ? "Guardar corrección" : "Guardar apunte"}
              </Button>
            </div>
          </Paper>
        )}

        {!ticketEditable && (
          <Typography variant="body2" className={classes.muted} style={{ marginBottom: 16 }}>
            Aquí solo se consulta la bitácora. Los apuntes nuevos se agregan desde el chat mientras el ticket está en atención.
          </Typography>
        )}

        <Divider style={{ marginBottom: 16 }} />

        <div className={classes.listWrap}>
          {loading ? (
            <Typography className={classes.muted}>Cargando bitácora...</Typography>
          ) : entries.length === 0 ? (
            <Typography className={classes.muted}>
              Aún no hay apuntes generales guardados para este cliente.
            </Typography>
          ) : (
            <List disablePadding>
              {entries.map((entry) => (
                <ListItem key={entry.id} className={classes.entryItem} disableGutters>
                  <Paper elevation={0} className={classes.entryCard}>
                    <div className={classes.entryHeader}>
                      <div>
                        <Typography variant="subtitle2">
                          {entry.user?.name || "Usuario"}
                        </Typography>
                        <Typography variant="caption" className={classes.muted}>
                          {formatDateTime(entry.eventAt || entry.createdAt)}
                        </Typography>
                        <div className={classes.entryMeta}>
                          {entry.ticketId ? (
                            <Chip
                              size="small"
                              label={`Ticket #${entry.ticket?.id || entry.ticketId}`}
                              color="primary"
                              variant="outlined"
                            />
                          ) : (
                            <Chip
                              size="small"
                              label="Registro general"
                              variant="outlined"
                            />
                          )}
                          {entry.ticket?.status ? (
                            <Chip
                              size="small"
                              label={`Estado: ${entry.ticket.status}`}
                              variant="outlined"
                            />
                          ) : null}
                        </div>
                      </div>
                      <div className={classes.entryActions}>
                        {canEditEntry(entry) ? (
                          <Tooltip title="Corregir este apunte">
                            <IconButton size="small" onClick={() => handleStartEdit(entry)}>
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        ) : null}
                      </div>
                    </div>
                    <ListItemText
                      primary={
                        <Typography variant="body2" className={classes.entryNote}>
                          {entry.note}
                        </Typography>
                      }
                    />
                  </Paper>
                </ListItem>
              ))}
            </List>
          )}
        </div>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} color="primary">
          Cerrar
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ContactHistoryModal;
